/**
 * c-newsletter-popup — メルマガ訴求ポップアップ + 左端ミニタブ
 *
 * 出典: Store_ALLUP-SHOP の c-image-popup。画像枠をメール登録フォームへ置き換え、
 *       登録完了時の状態遷移を追加している。
 *
 * 状態遷移 (localStorage キー: c-newsletter-popup:{sectionId}):
 *   (なし)                     → 初回。apparition-delay 秒後にポップアップ表示
 *   { state: 'minimized' }     → ポップアップ×後。ミニタブのみ表示、クリックで再表示
 *   { state: 'dismissed',
 *     hideUntil: timestamp }   → ミニタブ×後 / 登録完了後。hideUntil まで何も表示しない
 *
 * テーマエディタ (designMode) ではセクション選択で表示・選択解除でミニタブ化し、
 * localStorage には書き込まない。
 *
 * ポップアップはノンモーダル: 表示中も背景ページのスクロール・操作をブロックしない
 * (body への overflow-hidden 付与や trapFocus は行わない)。
 */
if (!customElements.get('c-newsletter-popup')) {
  customElements.define(
    'c-newsletter-popup',
    class CNewsletterPopup extends HTMLElement {
      connectedCallback() {
        this.modal = this.querySelector('.c-newsletter-popup__modal');
        this.dialog = this.querySelector('.c-newsletter-popup__dialog');
        this.minitab = this.querySelector('.c-newsletter-popup__minitab');
        if (!this.modal || !this.minitab) return;

        this.querySelector('.c-newsletter-popup__close').addEventListener('click', () => this.minimize());
        this.querySelector('.c-newsletter-popup__overlay').addEventListener('click', () => this.minimize());
        this.querySelector('.c-newsletter-popup__minitab-open').addEventListener('click', () => this.open(true));
        this.querySelector('.c-newsletter-popup__minitab-close').addEventListener('click', () => this.dismiss());
        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE' && !this.modal.hidden) this.minimize();
        });

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:section:select', (event) => {
            if (event.target.contains(this)) this.open();
          });
          document.addEventListener('shopify:section:deselect', (event) => {
            if (event.target.contains(this)) this.minimize();
          });
        } else {
          this.applyStoredState();
        }
      }

      disconnectedCallback() {
        if (this.appearTimer) clearTimeout(this.appearTimer);
      }

      get apparitionDelay() {
        return parseInt(this.dataset.apparitionDelay || '0', 10) * 1000;
      }

      get hideDays() {
        return parseInt(this.dataset.hideDays || '0', 10);
      }

      get storageKey() {
        return `c-newsletter-popup:${this.dataset.sectionId || 'default'}`;
      }

      /* Shopify の customer form は登録成功時に ?customer_posted=true を付けて戻る。
         form.posted_successfully? は form タグ内でしか参照できないため URL 側で判定する */
      get postedSuccess() {
        try {
          return new URLSearchParams(window.location.search).get('customer_posted') === 'true';
        } catch {
          return false;
        }
      }

      readState() {
        try {
          return JSON.parse(localStorage.getItem(this.storageKey));
        } catch {
          return null;
        }
      }

      writeState(state) {
        if (window.Shopify && window.Shopify.designMode) return;
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch {
          /* localStorage 無効環境では毎回初回表示になる (安全側) */
        }
      }

      clearState() {
        try {
          localStorage.removeItem(this.storageKey);
        } catch {
          /* noop */
        }
      }

      applyStoredState() {
        /* 登録完了直後は結果メッセージを見せ、以降は長期間出さない */
        if (this.postedSuccess) {
          this.writeState({ state: 'dismissed', hideUntil: Date.now() + 365 * 86400 * 1000 });
          this.open();
          return;
        }

        const state = this.readState();
        if (state && state.state === 'dismissed') {
          if (Date.now() < (state.hideUntil || 0)) return;
          this.clearState();
        } else if (state && state.state === 'minimized') {
          this.minitab.hidden = false;
          return;
        }
        this.appearTimer = setTimeout(() => this.open(), this.apparitionDelay);
      }

      /* focusDialog はミニタブ等のユーザー操作起点でのみ true にする
         (自動表示でフォーカスを奪うと閲覧・入力を中断させるため) */
      open(focusDialog = false) {
        this.minitab.hidden = true;
        this.modal.hidden = false;
        if (focusDialog && this.dialog) this.dialog.focus();
      }

      closeModal() {
        this.modal.hidden = true;
      }

      minimize() {
        if (this.appearTimer) clearTimeout(this.appearTimer);
        this.closeModal();
        this.minitab.hidden = false;
        this.querySelector('.c-newsletter-popup__minitab-open').focus();
        this.writeState({ state: 'minimized' });
      }

      dismiss() {
        this.closeModal();
        this.minitab.hidden = true;
        this.writeState({
          state: 'dismissed',
          hideUntil: Date.now() + this.hideDays * 86400 * 1000,
        });
      }
    }
  );
}
